import {
  DevicesLocationQuery,
  DeviceSort,
  DeviceSortDirection,
  DeviceSortField,
  Filter,
  GeoBox,
  KeyBucket
} from "generated/graphql";
import { UseDeviceAction } from "routes/Manage/Actions/useDeviceAction";
import { DeviceFilterState } from "elements";
import { GenericBucket } from "routes/Home/bucketTypes";
import {
  hasValue,
  UNKNOWN_VALUE,
  VALUE_NOT_SET
} from "utilities/filters/filters";
import { keyIter } from "utilities/objectUtils";
import { STAGED_LAST_CONFIG_REQUEST_TIME_MILLISECONDS } from "Constants";
import { subHours } from "date-fns";
import { ProvisioningState } from "elements/ConnectionStatusIndicator/ConnectionStatusIndicator";
import { PreFilter } from "routes/Manage/Inventory/List/useDevices";

export const mapFieldToGraphQL: { [key: string]: string } = {
  name: "name",
  serialNumber: "serialNumber",
  macAddress: "macAddress",
  ipv4: "internalIp",
  model: "hardwareModel",
  version: "softwareVersion",
  connected: "connected",
  room: "room",
  site: "site",
  type: "type",
  insight: "insight",
  id: "id",
  isUpdateAvailable: "isUpdateAvailable",
  updateVersion: "updateVersion",
  lastConfigRequestDate: "lastConfigRequestDate",
  lastDetected: "lastDetected",
  entitledUntilDate: "entitledUntilDate",
  dateRegistered: "dateRegistered",
  activeApplicationName: "activeApplicationName",
  lineNumber: "lineNumber",
  status: "status",
  serverType: "serverType",
  domain: "domain",
  serverAddress: "serverAddress",
  supportsSoftwareUpdate: "supportsSoftwareUpdate",
  supportsSettings: "supportsSettings",
  provisioningEnabled: "provisioningEnabled",
  entitlements: "entitlements"
};

export type DeviceFilterProps = DeviceFilterState & {
  id?: string;
};

const constructProvisionedStatusFilter = (
  filterState: DeviceFilterProps,
  key: keyof DeviceFilterState
) => {
  let lastConfigRequestDateFilter: Filter = {};
  const field = mapFieldToGraphQL["lastConfigRequestDate"];

  // get the current date, but for time only hours portion is of interest
  const currentDate = new Date();
  currentDate.setMinutes(0, 0, 0);
  const activeDate = subHours(currentDate, 24).toISOString();
  const stagedDate = new Date(
    STAGED_LAST_CONFIG_REQUEST_TIME_MILLISECONDS
  ).toISOString();

  switch (filterState[key]) {
    case ProvisioningState.ACTIVE:
      lastConfigRequestDateFilter = {
        field,
        ge: activeDate
      };
      break;
    case ProvisioningState.INACTIVE:
      lastConfigRequestDateFilter = {
        AND: [
          { field, lt: activeDate },
          { field, ne: stagedDate }
        ]
      };
      break;
    case ProvisioningState.STAGED:
      lastConfigRequestDateFilter = {
        field,
        eq: stagedDate
      };
      break;
  }

  return lastConfigRequestDateFilter;
};

export const constructFilterList = (
  filterState: DeviceFilterProps = {},
  tenantId?: string,
  bounds?: GeoBox
) => {
  let filters: Array<Filter> = [];

  if (tenantId) {
    filters.push({
      field: "tenantId",
      eq: tenantId
    });
  }

  if (bounds) {
    filters.push({
      field: "geohash",
      within: bounds
    });
  }

  keyIter(filterState).forEach(key => {
    if (hasValue(filterState, key)) {
      if (key === "provisioningState") {
        filters.push(constructProvisionedStatusFilter(filterState, key));
        return;
      }

      if (filterState[key] === UNKNOWN_VALUE) {
        filters.push({
          OR: [
            { field: mapFieldToGraphQL[key], exists: false },
            { field: mapFieldToGraphQL[key], eq: "Unknown" }
          ]
        });

        return;
      }

      if (key === "sort") {
        return;
      }

      const value = filterState[key];

      if (key === "model" && value === "") {
        filters.push({
          OR: [
            { field: mapFieldToGraphQL[key], exists: false },
            { field: mapFieldToGraphQL[key], eq: "Unknown" }
          ]
        });

        return;
      }

      filters.push({
        field: mapFieldToGraphQL[key],
        eq: value
      });
    }
  });

  return filters;
};

/**
 * Transform preFilter argument into GraphQL filters.
 *
 * Allows the preFilter argument on useDevices to accept more robust
 * (but still type-safe) filter conditions passed directly into the hook.
 *
 * {
 *   model: "P15",
 *   entitledUntilDate: {OR: [{eq: null}, {lt: "9982431102934"}]}
 * }
 *
 * transform to:
 * [
 *   {field: "hardwareModel", eq: "P15"},
 *   {
 *     OR: [
 *       { field: "entitledUntilDate", eq: null}
 *       { field: "entitledUntilDate", lt: "9982431102934"}
 *     ]
 *   }
 */
export const constructPreFilter = (preFilter: PreFilter): Array<Filter> => {
  const filter: Array<Filter> = [];
  keyIter(preFilter).forEach((key: keyof PreFilter) => {
    const field = mapFieldToGraphQL[key];
    let value = preFilter[key];

    if (null === value) {
      filter.push({ field, eq: null });
      return;
    }

    if ("string" === typeof value) {
      if (field.includes(".lower")) {
        value = value.toLowerCase();
      }

      filter.push({ field, eq: value });
      return;
    }

    if (value && value.OR) {
      value.OR = value.OR.map((f: Filter) => ({ ...f, field }));
      filter.push(value);
      return;
    }

    if (value && value.AND) {
      value.AND = value.AND.map((f: Filter) => ({ ...f, field }));
      filter.push(value);
      return;
    }
  });

  return filter;
};

export const constructSortList = (filterState: DeviceFilterState) => {
  let sort: DeviceSort = {
    fields: [] as Array<DeviceSortField>
  };

  if (hasValue(filterState, "sort")) {
    const sortValue = filterState["sort"]!;
    sort.fields!.push({
      name: mapFieldToGraphQL[sortValue.field],
      direction:
        sortValue.direction === "ASC"
          ? DeviceSortDirection.Asc
          : DeviceSortDirection.Desc
    });
  }

  return sort;
};

export const getFieldAndCountWithEntries = <T>(entries: Array<T>) => (
  selector: (entry: T) => string
) => {
  let counted = entries.reduce((countMap: Map<string, number>, entry) => {
    let deviceField = selector(entry);
    if (deviceField === "") deviceField = VALUE_NOT_SET;
    else if (deviceField === null) deviceField = UNKNOWN_VALUE;

    let fieldCount = countMap.get(deviceField);
    if (fieldCount) {
      fieldCount++;
    } else {
      fieldCount = 1;
    }
    countMap.set(deviceField, fieldCount);
    return countMap;
  }, new Map());

  return Array.from(counted.entries())
    .map(([key, count]) => ({
      value: key,
      count: count
    }))
    .sort((a, b) => b.count - a.count);
};

export const getSitesFromActions = (actions: Array<UseDeviceAction>) => {
  let counted = actions.reduce((countMap: Map<string, number>, action) => {
    let sites = action.locations;

    sites.forEach(site => {
      if (site === "") site = VALUE_NOT_SET;
      else if (site === null) site = UNKNOWN_VALUE;

      let fieldCount = countMap.get(site);
      if (fieldCount) {
        fieldCount++;
      } else {
        fieldCount = 1;
      }
      countMap.set(site, fieldCount);
    });
    return countMap;
  }, new Map());

  return Array.from(counted.entries())
    .map(([key, count]) => ({
      value: key,
      count: count
    }))
    .sort((a, b) => b.count - a.count);
};

export const verifyAndFormatAggregates = (
  aggregates: DevicesLocationQuery["deviceAggregations"]
) => (field: string) => {
  let buckets: Array<GenericBucket> = [];
  let index: number | undefined;
  let missingCount = 0;

  aggregates.forEach(
    (agg: DevicesLocationQuery["deviceAggregations"][number], i: number) => {
      if (agg.field === field) {
        index = i;
      }
    }
  );

  if (
    index !== undefined &&
    aggregates &&
    aggregates[index] &&
    aggregates[index].field === field
  ) {
    buckets = aggregates[index].buckets as Array<GenericBucket>;
    const keyBucketFields = [
      "connected",
      "site",
      "hardwareModel",
      "softwareVersion"
    ];

    buckets.forEach((bucket: GenericBucket) => {
      if (bucket.__typename === "FieldMissingBucket") {
        missingCount = bucket.count;
      }
      // Changes are made to make sure __typename is always present, but leaving below workaround just in case
      // In some conditions, the __typename is removed. If that's the case, add it back in
      // For now, using white-list to add back in so it doesn't add it to something it shouldn't
      if (
        index !== undefined &&
        bucket.__typename !== "KeyBucket" &&
        bucket.__typename !== "FieldMissingBucket"
      ) {
        if (
          keyBucketFields.includes(aggregates[index].field) &&
          (bucket as KeyBucket).key
        ) {
          // @ts-ignore
          bucket.__typename = "KeyBucket";
        }
      }
    });
  }

  return buckets
    .filter(
      (model: GenericBucket): model is KeyBucket =>
        model.__typename === "KeyBucket"
    )
    .map((k: KeyBucket) => ({
      value: k.key,
      count: k.key === UNKNOWN_VALUE ? k.count + missingCount : k.count
    }));
};
